/**
 * Track pictures.
 *
 * The images live in the client's own public folder (`public/img/tracks/`), and the file name is the one the
 * track file writes down (`backgroundImage`): the track carries its picture's name and format with it, so
 * nothing here has to guess an extension from the id.
 */
export function trackImage(file)
{
  return `/img/tracks/${file}`
}

/**
 * The shrunk copy of a picture, as a page shows it (see scripts/build-track-previews.mjs): same name, always
 * WebP. The original is painted at one pixel per world unit — a file of several megabytes — and that size
 * only pays off where the pixels are stretched over the world (the replay, the editor).
 */
export function trackPreviewImage(file)
{
  return `/img/tracks/preview/${file.replace(/\.[^./]+$/, '')}.webp`
}

/** The formats a track picture can be in, tried in order when only the track's id is known. */
const PICTURE_FORMATS = ['webp', 'jpg', 'jpeg', 'png']

/** The first of `urls` that loads, or null when none does. */
function firstPictureThatLoads(urls)
{
  return new Promise((resolve) => {
    let index = 0
    const tryNext = () => {
      if (index >= urls.length) { resolve(null); return }
      const url = urls[index++]
      const image = new Image()
      image.onload = () => resolve(url)
      image.onerror = tryNext
      image.src = url
    }
    tryNext()
  })
}

/**
 * The addresses of a track's picture, best first: the shrunk copy a page shows, then the original in
 * whatever format the track was exported in.
 */
export function trackPictureCandidates(trackId)
{
  return [
    trackPreviewImage(trackId),
    ...PICTURE_FORMATS.map(format => trackImage(`${trackId}.${format}`)),
  ]
}

/** The address of the picture to show for a track, or null when the site has none. */
export function trackPicture(trackId)
{
  return firstPictureThatLoads(trackPictureCandidates(trackId))
}

/**
 * The working background the track editor paints behind the drawing: a picture named after the track's
 * id in `public/img/tracks/editor_backgrounds/`, kept apart from the track's own finished picture (the
 * one the game shows). It is a working aid, not an asset the track carries, so nothing names its format
 * and every candidate is tried until one loads — null when the track has none.
 */
export function trackEditorBackground(trackId)
{
  const base = `/img/tracks/editor_backgrounds/${trackId}`
  return firstPictureThatLoads(PICTURE_FORMATS.map(format => `${base}.${format}`))
}
